import React, { Suspense, useRef, useEffect } from 'react'
import { Canvas, useThree } from '@react-three/fiber'
import { OrbitControls, Preload, useHelper } from '@react-three/drei'
import * as THREE from 'three'
import { PointLightHelper } from 'three'
import Model from './Model'
import ScenePlayer from './ScenePlayer'
import TimelineVisualizer from './TimelineVisualizer'
import WaterParticles from './WaterParticles'
import WaterLine from './WaterLine'
import Loader from './Loader'

// --- Helper Component for Light Visualization --- 
// This component MUST be rendered INSIDE the Canvas
function VisiblePointLight({ lightRef }) {
  // --- Comment out or remove the useHelper call to hide the visual --- 
  // useHelper(lightRef, PointLightHelper, 0.5, 'red'); 
  // -------------------------------------------------------------------
  return null; 
}
// ---------------------------------------------

// --- Helper Component to Set Camera Ref --- 
function CameraRefSetter({ cameraRef }) {
  const { camera } = useThree();
  useEffect(() => {
    if (cameraRef) {
      cameraRef.current = camera;
    }
    // Optional: Cleanup ref on unmount
    // return () => { if (cameraRef) cameraRef.current = null; }
  }, [camera, cameraRef]);
  return null;
}
// ----------------------------------------

// <<< Define water level constant >>>
const WATER_SURFACE_Y = 0.5; 

// Accept props from App.jsx, including the ref for OrbitControls and ScenePlayer props
const SylentsAnimation = ({
    scene,
    meshGroups,
    visibilityState,
    orbitControlsRef,
    scenePlayerRef,
    activeTimeline,
    onTimelineChange,
    // ScenePlayer props
    activeSceneId,
    timelineRef,
    isTimelineReady,
    // Lighting props
    ambientIntensity,
    dirIntensity,
    dirPosition,
    dirColor,
    // Fill Light Props
    fillIntensity,
    fillPosition,
    fillColor,
    // CameraRef
    cameraRef,
    onReady,
    modelPosition,
    // <<< Add Water & Particle Props >>>
    waterEnabled,
    waterColor,
    waterOpacity,
    particleColor,
    particleSize,
    particleDensity,
    particleIntakeYOffset,
    // <<< Add new props >>>
    lineWidth,
    waveHeight,
    waveSpeed,
    waveFrequency
}) => {
  // Ref for the point light
  const pointLightRef = useRef();

  // <<< Log props related to ScenePlayer rendering >>>
  console.log(`SylentsAnimation: Rendering. isTimelineReady=${isTimelineReady}, typeof onReady=${typeof onReady}`);

  // Return a fragment containing the scene elements, NOT a Canvas
  return (
    <>
        {/* --- Add Fog --- */}
        <fog attach="fog" args={['#0a192f', 1, 15]} /> 

        {/* --- Set the cameraRef using the helper component --- */}
        <CameraRefSetter cameraRef={cameraRef} /> 

        {/* --- Lights --- */}
        <ambientLight intensity={ambientIntensity} />
        <directionalLight
          position={dirPosition}
          intensity={dirIntensity}
          color={dirColor}
        castShadow
          shadow-mapSize-width={1024}
          shadow-mapSize-height={1024}
        />
        <directionalLight
          position={fillPosition} 
          intensity={fillIntensity}
          color={fillColor}
        castShadow={false}
        />
        <pointLight 
          ref={pointLightRef} 
          position={[2, 1.5, 2]} 
          intensity={1.5}
          color={"yellow"} 
        />
        <VisiblePointLight lightRef={pointLightRef} />

        {/* --- Render Water Line conditionally --- */}
        {waterEnabled && (
            <WaterLine 
                color={waterColor} 
                modelPosition={modelPosition}
                lineWidth={lineWidth}
                waveHeight={waveHeight}
                waveSpeed={waveSpeed}
                waveFrequency={waveFrequency}
            />
        )}

        <Suspense fallback={<Loader />}>
          <Model
             scene={scene}
             meshGroups={meshGroups}
             visibilityState={visibilityState}
             modelPosition={modelPosition} 
          />
        
        {activeSceneId === 'waterFlow' && 
            <WaterParticles 
                modelPosition={modelPosition} 
                waterSurfaceY={WATER_SURFACE_Y} 
                particleColor={particleColor}
                particleSize={particleSize}
                particleDensity={particleDensity}
                particleIntakeYOffset={particleIntakeYOffset}
            />
        }

          <Preload all />
        </Suspense>
      
      {/* OrbitControls are now rendered directly by SceneViewer, remove from here if redundant */}
      {/* <OrbitControls ref={orbitControlsRef} enablePan={false} /> */}

      {/* Scene Player - RENDERED INSIDE CANVAS CONTEXT */}
        {isTimelineReady && (
            <ScenePlayer 
                ref={scenePlayerRef}
                onTimelineChange={onTimelineChange}
                orbitControlsRef={orbitControlsRef}
                meshGroups={meshGroups}
                timelineRef={timelineRef}
              onReady={onReady}
            />
        )}

      {/* Optional ground plane can remain */}
      
      {/* TimelineVisualizer is rendered outside the Canvas in SceneViewer's structure usually, */}
      {/* If it needs Canvas context, it must stay, otherwise it can be moved. */}
      {/* Assuming it doesn't need context for now, as it was outside before */}
      {/* <div className="absolute bottom-0 left-0 right-0 p-4 z-10">
        <TimelineVisualizer activeTimeline={activeTimeline} />
      </div> */}
    </>
  )
}

export default SylentsAnimation 